import { Link } from "gatsby"
import React, { useContext, useEffect } from "react"
import styled from "styled-components"
import { Button } from "@zendeskgarden/react-buttons"
import SearchIcon from "@zendeskgarden/svg-icons/src/16/search-stroke.svg"
import { collections } from "../../lib/prop-types"
import LogoLink from "./logoLink"
import SearchModal from "../search/searchModal"
import SearchModalContext from "../search/searchModalContext"

const Container = styled.header`
  position: relative;
  z-index: 10;
  display: grid;
  grid-gap: ${props => props.theme.space.md};
  grid-template-areas: "logo nav search";
  grid-template-columns: ${props =>
    `${props.theme.layout.leftColumn.width} max-content auto`};
  align-items: center;
  padding: 0 ${props => props.theme.space.md};
  background-color: ${props => props.theme.colors.header.background};
  box-shadow: ${({ theme }) =>
    theme.shadows.lg(
      `${theme.space.base * 4}px`,
      `${theme.space.base * 6}px`,
      theme.colors.header.shadow
    )};
`

const LogoCol = styled.div`
  grid-area: logo;
`

const NavCol = styled.div`
  grid-area: nav;
`

const NavLink = styled(Link)`
  display: inline-block;
  padding: ${props => props.theme.space.base * 7}px;
  color: ${props => props.theme.colors.header.textColor};
  white-space: nowrap;
  border-color: transparent;
  border-bottom-width: ${props => props.theme.borderWidths.md};
  border-bottom-style: ${props => props.theme.borderStyles.solid};
  ${props =>
    props.partiallyActive &&
    `border-color: ${props.theme.colors.header.desktopNavLinkActive};
     color: ${props.theme.colors.header.desktopNavLinkActive};
    `}

  &:hover,
  &:focus {
    color: ${props => props.theme.colors.header.desktopNavLinkHover};
    text-decoration: none;
  }

  &:focus {
    box-shadow: ${props => props.theme.focusBoxShadow};
  }
`

const SearchCol = styled.div`
  grid-area: search;
  width: 100%;
  max-width: ${props => props.theme.layout.search.input.width};
`

const SearchButton = styled(Button)`
  display: flex;
  padding: 0 ${props => props.theme.space.sm};
  color: ${props => props.theme.colors.search.buttonText};
  background-color: ${props => props.theme.colors.header.background};
  border-color: ${props => props.theme.colors.search.buttonBorder};

  &:hover,
  &:focus {
    color: ${props => props.theme.colors.search.buttonText};
    background-color: ${props => props.theme.colors.search.buttonHover};
    border-color: ${props => props.theme.colors.search.buttonBorderHover};
  }
`

const SearchButtonLabel = styled.span`
  flex-grow: 1;
  text-align: left;
`

const SearchKeyboardHint = styled.div`
  padding: 3px;
  color: ${props => props.theme.colors.search.searchBarIcon};
  font-size: ${props => props.theme.fontSizes.xs};
  line-height: 1;
  text-align: center;
  background-color: ${props => props.theme.colors.search.slashIconBackground};
  border: ${props => props.theme.borders.sm}
    ${props => props.theme.colors.search.slashIconBorder};
  border-radius: ${props => props.theme.borderRadii.sm};

  && {
    width: ${props => props.theme.space.md};
    height: ${props => props.theme.space.md};
  }
`

const StyledSearchIcon = styled(SearchIcon)`
  color: ${props => props.theme.colors.search.searchBarIcon};
`

const Desktop = ({ collection }) => {
  const { openSearchModal, setOpenSearchModal } = useContext(SearchModalContext)

  function handleSearchKeyDown(e) {
    const forwardSlashKeyPressed = e.keyCode === 191
    if (forwardSlashKeyPressed) {
      e.preventDefault() // override Firefox's default behaviour of opening the "quick find" dialog
      setOpenSearchModal(true)
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleSearchKeyDown, false)

    return () => {
      document.removeEventListener("keydown", handleSearchKeyDown, false)
    }
  }, [])

  return (
    <Container data-testid="dev-docs-header-desktop-container">
      <LogoCol>
        <LogoLink to="/" width={180} />
      </LogoCol>
      <NavCol>
        <NavLink
          aria-current={collection === "documentation"}
          to="/documentation/"
          partiallyActive={collection === "documentation"}
        >
          Documentation
        </NavLink>
        <NavLink
          aria-current={collection === "api-reference"}
          to="/api-reference/"
          partiallyActive={collection === "api-reference"}
        >
          API Reference
        </NavLink>
      </NavCol>
      <SearchCol data-testid="dev-docs-header-search-bar">
        <SearchButton isStretched onClick={() => setOpenSearchModal(true)}>
          <Button.StartIcon>
            <StyledSearchIcon />
          </Button.StartIcon>
          <SearchButtonLabel>Search</SearchButtonLabel>
          <Button.EndIcon aria-hidden="true">
            <SearchKeyboardHint>/</SearchKeyboardHint>
          </Button.EndIcon>
        </SearchButton>

        {openSearchModal && (
          <SearchModal
            closeModal={() => {
              setOpenSearchModal(false)
            }}
            collection={collection}
          />
        )}
      </SearchCol>
    </Container>
  )
}

Desktop.propTypes = {
  collection: collections.isRequired
}

export default Desktop
